import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CeRequestStateService } from '../../services/ce-request-state.service';
import { DictionariesApiService } from '../../services/dictionaries-api.service';
import { HttpNotificationService } from '../../services/http-notification.service';
import { ProjectDto, ProjectsApiService, UpdateProjectCommand } from '../../services/projects-api.service';
import { CurrentUserService } from '../../services/current-user.service';
import { FlowStep, getProjectTeamSteps } from '../../data/flow-steps';

const SUBMITTED_STATUS_TYPE = 1;
const ISSUED_STATUS_TYPE = 4;
type DetailsRoute = '/new-project' | '/existing-project' | null;

@Component({
  selector: 'app-project-team',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatAutocompleteModule, MatTooltipModule],
  templateUrl: './project-team.html',
  styleUrl: './project-team.scss',
})
export class ProjectTeamComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly state = inject(CeRequestStateService);
  private readonly dictionariesApi = inject(DictionariesApiService);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly notifications = inject(HttpNotificationService);
  private readonly currentUserService = inject(CurrentUserService);
  protected readonly projectId = signal<number | null>(null);
  protected readonly project = signal<ProjectDto | null>(null);
  protected readonly projectStatusType = signal<number | null>(null);
  protected readonly isSubmittedStatus = computed(() => {
    const status = this.projectStatusType();
    return status === SUBMITTED_STATUS_TYPE || status === ISSUED_STATUS_TYPE;
  });
  protected readonly canAccessResponse = computed(() => {
    const isProjectEngineer = this.currentUserService.hasAnyRole(['project_engineer', 'project_engineers']);
    if (!isProjectEngineer) {
      return true;
    }

    const status = this.projectStatusType();
    return typeof status === 'number' && status >= SUBMITTED_STATUS_TYPE;
  });
  protected readonly canSave = computed(() => !!this.projectId());
  protected readonly isSaving = signal(false);

  protected readonly steps = computed(() =>
    getProjectTeamSteps(
      this.detailsRoute(),
      this.state.flowProgress().questionnaireVisited || !!this.projectId(),
      this.state.flowProgress().responseVisited || !!this.projectId(),
      this.canAccessResponse(),
    ),
  );
  protected readonly detailsRoute = computed<DetailsRoute>(() => {
    // For existing project editing, always resolve details route from isNewProject field.
    if (this.projectId()) {
      return this.formData().isNewProject ? '/new-project' : '/existing-project';
    }

    return this.state.flowProgress().detailsRoute;
  });

  protected readonly teams = signal<string[]>([]);
  protected readonly showAllProjectTeamOptions = signal(false);

  protected readonly formData = signal({
    projectTeam: this.state.draftForm().projectTeam,
    afeNumber: this.state.draftForm().afeNumber,
    isNewProject: this.state.draftForm().isNewProject,
  });
  protected readonly filteredTeams = computed(() => {
    if (this.showAllProjectTeamOptions()) {
      return this.teams();
    }

    const query = this.formData().projectTeam.trim().toLowerCase();
    if (!query) {
      return this.teams();
    }

    return this.teams().filter(team => team.toLowerCase().includes(query));
  });
  protected submitAttempted = signal(false);

  constructor() {
    void this.currentUserService.load();
    void this.loadProjectTeams();
    const paramId = this.route.snapshot.queryParamMap.get('projectId');
    if (paramId) {
      const id = Number(paramId);
      this.projectId.set(id);
      void this.loadProjectById(id);
    }
  }

  private async loadProjectById(id: number): Promise<void> {
    try {
      const dto = await firstValueFrom(this.projectsApi.getProjectById(id));
      this.project.set(dto);
      this.projectStatusType.set(dto.projectStatusType ?? 0);
      const resolvedIsNewProject = dto.isNewProject ?? false;
      this.formData.update(f => ({
        ...f,
        projectTeam: dto.projectTeam ?? f.projectTeam,
        afeNumber: dto.afeNumber ?? f.afeNumber,
        isNewProject: resolvedIsNewProject,
      }));
      this.state.draftForm.update(d => ({
        ...d,
        isNewProject: resolvedIsNewProject,
      }));
    } catch (error) {
      console.error(error);
      // silently ignore load errors
    }
  }

  protected async save(): Promise<void> {
    const id = this.projectId();
    if (!id || this.isSaving()) {
      return;
    }

    this.submitAttempted.set(true);
    if (!this.hasRequiredFields()) {
      return;
    }

    let currentProject: ProjectDto;
    try {
      // Re-fetch right before PUT to avoid overwriting concurrent edits with stale cached values.
      currentProject = await firstValueFrom(this.projectsApi.getProjectById(id));
      this.project.set(currentProject);
    } catch (error) {
      console.error(error);
      this.notifications.showError('Unable to load project data for save.');
      return;
    }

    const dictionaryIds = await this.resolveProjectDictionaryIds(currentProject);
    if (!dictionaryIds) {
      this.notifications.showError('One or more dictionary values are outdated. Please open project details and reselect dropdown values.');
      return;
    }

    const payload: UpdateProjectCommand = {
      projectTeam: this.formData().projectTeam,
      afeNumber: this.formData().afeNumber,
      projectTitle: currentProject.projectTitle ?? '',
      estimateTitle: currentProject.estimateTitle ?? '',
      projectNumber: currentProject.projectNumber ?? '',
      projectCode: currentProject.projectCode ?? '',
      estimateRequestTypeId: dictionaryIds.estimateRequestTypeId,
      projectManagementTypeId: dictionaryIds.projectManagementTypeId,
      cpdepPhaseId: dictionaryIds.cpdepPhaseId,
      designProgressId: dictionaryIds.designProgressId,
      projectCompletionDate: this.toIsoOrEmpty(currentProject.projectCompletionDate),
      deadlineDate: this.toIsoOrEmpty(currentProject.deadlineDate),
      areaId: dictionaryIds.areaId,
      unitId: dictionaryIds.unitId,
      ibpYearId: dictionaryIds.ibpYearId,
      turnaroundYearId: dictionaryIds.turnaroundYearId,
    };

    this.isSaving.set(true);
    try {
      await firstValueFrom(this.projectsApi.updateProject(id, payload));
      this.state.draftForm.update(d => ({
        ...d,
        projectTeam: this.formData().projectTeam,
        afeNumber: this.formData().afeNumber,
      }));
      this.project.update(project => project
        ? {
          ...project,
          projectTeam: this.formData().projectTeam,
          afeNumber: this.formData().afeNumber,
        }
        : project,
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.isSaving.set(false);
    }
  }

  protected goBack(): void {
    this.router.navigate(['/']);
  }

  protected continue(): void {
    if (this.isSubmittedStatus()) {
      const next = this.formData().isNewProject ? '/new-project' : '/existing-project';
      this.state.setDetailsRoute(next);
      const id = this.projectId();
      const queryParams = id ? { projectId: id } : undefined;
      this.router.navigate([next], queryParams ? { queryParams } : {});
      return;
    }

    this.submitAttempted.set(true);

    if (!this.hasRequiredFields()) {
      return;
    }

    this.state.draftForm.update(d => ({
      ...d,
      projectTeam: this.formData().projectTeam,
      afeNumber: this.formData().afeNumber,
      isNewProject: this.formData().isNewProject,
    }));
    const next = this.formData().isNewProject ? '/new-project' : '/existing-project';
    this.state.setDetailsRoute(next);
    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate([next], queryParams ? { queryParams } : {});
  }

  protected openProjectTeamOptions(trigger: MatAutocompleteTrigger): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.showAllProjectTeamOptions.set(true);
    trigger.openPanel();
  }

  protected onProjectTeamInput(value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.showAllProjectTeamOptions.set(false);
    this.formData.update(f => ({ ...f, projectTeam: value }));
  }

  protected onProjectTeamBlur(): void {
    setTimeout(() => {
      this.showAllProjectTeamOptions.set(false);
    }, 120);
  }

  protected onAfeNumberInput(value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    // Filter to only digits
    const digitsOnly = value.replace(/[^0-9]/g, '');
    this.formData.update(f => ({ ...f, afeNumber: digitsOnly }));
  }

  protected blockNonDigit(event: KeyboardEvent): void {
    if (this.isSubmittedStatus()) {
      event.preventDefault();
      return;
    }

    if (event.key.length === 1 && !/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  protected isStepClickable(step: FlowStep): boolean {
    return !!(step.completed && step.route);
  }

  protected goToStep(step: FlowStep): void {
    if (!this.isStepClickable(step)) {
      return;
    }

    if (step.number === 2) {
      const detailsTarget = this.formData().isNewProject ? '/new-project' : '/existing-project';
      const id = this.projectId();
      const queryParams = id ? { projectId: id } : undefined;
      this.router.navigate([detailsTarget], queryParams ? { queryParams } : {});
      return;
    }

    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate([step.route!], queryParams ? { queryParams } : {});
  }

  private async loadProjectTeams(): Promise<void> {
    try {
      const teams = await firstValueFrom(this.dictionariesApi.getProjectTeams());
      this.teams.set(teams);
    } catch (error) {
      console.error(error);
      this.teams.set([]);
    }
  }

  private hasRequiredFields(): boolean {
    const f = this.formData();
    const afeNumber = f.afeNumber.trim();
    return !!(f.projectTeam && afeNumber && afeNumber.length === 10 && /^[0-9]+$/.test(afeNumber));
  }

  private toIsoOrEmpty(value?: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString();
  }

  private async resolveProjectDictionaryIds(project: ProjectDto): Promise<{
    estimateRequestTypeId: number;
    projectManagementTypeId: number;
    cpdepPhaseId: number;
    designProgressId: number;
    areaId: number;
    unitId: number;
    ibpYearId: number;
    turnaroundYearId: number;
  } | null> {
    const [estimateRequestTypes, projectManagementTypes, cpdepPhases, designProgresses, areas, units, ibpYears, turnaroundYears] = await Promise.all([
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('EstimateRequestTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('ProjectManagementTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('CpdepPhases')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('DesignProgresses')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Areas')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Units')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('IbpYears')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('TurnaroundYears')),
    ]);

    const ids = {
      estimateRequestTypeId: this.dictionariesApi.findDictionaryId(estimateRequestTypes, project.estimateRequestType ?? ''),
      projectManagementTypeId: this.dictionariesApi.findDictionaryId(projectManagementTypes, project.projectManagementType ?? ''),
      cpdepPhaseId: this.dictionariesApi.findDictionaryId(cpdepPhases, project.cpdepPhase ?? ''),
      designProgressId: this.dictionariesApi.findDictionaryId(designProgresses, project.designProgress ?? ''),
      areaId: this.dictionariesApi.findDictionaryId(areas, project.area ?? ''),
      unitId: this.dictionariesApi.findDictionaryId(units, project.unit ?? ''),
      ibpYearId: this.dictionariesApi.findDictionaryId(ibpYears, project.ibpYear ?? ''),
      turnaroundYearId: this.dictionariesApi.findDictionaryId(turnaroundYears, project.turnaroundYear ?? ''),
    };

    if (Object.values(ids).some(id => id <= 0)) {
      return null;
    }

    return ids;
  }
}

